import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Spinner } from "@/components/Spinner/Spinner";
import { RainAggregates, RainObservation, RollingWindow } from "@/endpoints/rain/rain";

export const POPUP_ANIM = {
	initial: { opacity: 0, y: 6, scale: 0.98 },
	animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.1, ease: "easeOut" } },
	exit: { opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.1, ease: "easeIn" } },
};

export const formatTime = (timestamp: string) => {
	const date = new Date(timestamp);
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	return `${date.getMonth() + 1}/${date.getDate()} ${hours}:${minutes}`;
};

export const formatDate = (timestamp: string) => {
	const date = new Date(timestamp);
	return `${date.getDate()}/${date.getMonth() + 1}`;
};

export const computeStats = (data: { value: number }[]) => {
	if (data.length === 0) return null;
	const values = data.map(point => point.value);
	const total = values.reduce((sum, value) => sum + value, 0);
	const max = Math.max(...values);
	const nonZero = values.filter(value => value > 0).length;
	return { total, max, nonZero, count: values.length };
};

export const computeDailyStats = (windows: RollingWindow[]) => {
	if (windows.length === 0) return null;
	const values = windows.map(window => window.value);
	const total = values.reduce((sum, value) => sum + value, 0);
	const max = Math.max(...values);
	const wetDays = values.filter(value => value > 0).length;
	return { total, max, wetDays, totalDays: values.length };
};

export const StatCard = ({ label, value }: { label: string; value: string }) => (
	<div className="bg-muted/50 rounded-md px-2 py-1.5">
		<p className="text-[10px] text-muted-foreground leading-none">{label}</p>
		<p className="text-xs font-semibold text-foreground mt-0.5 leading-none">{value}</p>
	</div>
);

export const RainChart = ({
	data,
	tickFormatter,
	tooltipLabelFormatter,
	tooltipValueFormatter,
	height = 130,
}: {
	data: object[];
	tickFormatter: (ts: string) => string;
	tooltipLabelFormatter: (ts: string) => string;
	tooltipValueFormatter: (value: number) => [string, string];
	height?: number;
}) => (
	<ResponsiveContainer width="100%" height={height}>
		<BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
			<defs>
				<linearGradient id="rainBarGradient" x1="0" y1="0" x2="0" y2="1">
					<stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
					<stop offset="95%" stopColor="#06b6d4" stopOpacity={0.3} />
				</linearGradient>
			</defs>
			<XAxis
				dataKey="ts"
				tickFormatter={tickFormatter}
				tick={{ fontSize: 9 }}
				interval="equidistantPreserveStart"
				tickCount={5}
				tickLine={false}
				axisLine={false}
			/>
			<YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={40} />
			<Tooltip
				labelFormatter={tooltipLabelFormatter}
				formatter={tooltipValueFormatter}
				contentStyle={{ fontSize: 11, padding: "4px 8px", borderRadius: 8 }}
			/>
			<Bar dataKey="value" fill="url(#rainBarGradient)" radius={[2, 2, 0, 0]} />
		</BarChart>
	</ResponsiveContainer>
);

export type RainContentData =
	| {
			mode: "daily";
			rollingWindows: RollingWindow[];
			aggregates?: RainAggregates | null;
			timePeriodLabel?: string;
	  }
	| { mode: "raw"; timeseries: RainObservation[]; timePeriodLabel?: string };

interface RainContentProps {
	loading: boolean;
	data: RainContentData | null;
}

export const RainContent: React.FC<RainContentProps> = ({ loading, data }) => {
	if (loading) {
		return (
			<div className="flex items-center justify-center h-[140px]">
				<Spinner size={20} messages={[]} loaderClassName="text-muted-foreground" />
			</div>
		);
	}

	if (!data) return null;

	if (data.mode === "daily") {
		if (data.rollingWindows.length === 0) {
			return (
				<div className="flex items-center justify-center h-[80px] text-xs text-muted-foreground">
					No rain data available for this location
				</div>
			);
		}
		const dailyStats = computeDailyStats(data.rollingWindows);
		return (
			<>
				{(dailyStats || data.aggregates) && (
					<div className="grid grid-cols-3 gap-1.5 mb-3">
						<StatCard
							label="Total"
							value={`${(data.aggregates?.totalMm ?? dailyStats?.total ?? 0).toFixed(1)} mm`}
						/>
						<StatCard label="Peak day" value={`${(dailyStats?.max ?? 0).toFixed(1)} mm`} />
						<StatCard label="Wet days" value={`${dailyStats?.wetDays}/${dailyStats?.totalDays}`} />
					</div>
				)}
				<div className="flex items-center justify-between mb-1.5">
					<span className="text-[10px] text-muted-foreground font-medium">
						{data.timePeriodLabel} — daily totals
					</span>
					<span className="text-[10px] text-muted-foreground">mm/day</span>
				</div>
				<RainChart
					data={data.rollingWindows}
					tickFormatter={formatDate}
					tooltipLabelFormatter={(timestamp: string) => {
						const date = new Date(timestamp);
						return date.toLocaleDateString(undefined, {
							day: "numeric",
							month: "short",
							year: "numeric",
						});
					}}
					tooltipValueFormatter={(value: number) => [`${value.toFixed(1)} mm`, "Daily total"]}
				/>
			</>
		);
	}

	if (data.timeseries.length === 0) {
		return (
			<div className="flex items-center justify-center h-[80px] text-xs text-muted-foreground">
				No rain data available for this location
			</div>
		);
	}
	const stats = computeStats(data.timeseries);
	return (
		<>
			{stats && (
				<div className="grid grid-cols-3 gap-1.5 mb-3">
					<StatCard label="Total" value={`${stats.total.toFixed(1)} mm`} />
					<StatCard label="Peak" value={`${stats.max.toFixed(1)} mm/h`} />
					<StatCard label="Wet intervals" value={`${stats.nonZero}/${stats.count}`} />
				</div>
			)}
			<div className="flex items-center justify-between mb-1.5">
				<span className="text-[10px] text-muted-foreground font-medium">{data.timePeriodLabel}</span>
				<span className="text-[10px] text-muted-foreground">mm/h</span>
			</div>
			<RainChart
				data={data.timeseries}
				tickFormatter={formatTime}
				tooltipLabelFormatter={formatTime}
				tooltipValueFormatter={(value: number) => [`${value.toFixed(2)} mm/h`, "Rain"]}
			/>
		</>
	);
};
